import { makeAutoObservable } from "mobx";
import { ReactNode } from "react";
import _ from "lodash";
import IScale from "../../../../models/IScale";
import ViewModel from "../../../ViewModel";
import ScaleService from "../../../../services/ScaleService";

export type SortDirections = 'ascending' | 'descending';

export const enum SortableColumns {
    id = 'id',
    name = 'name',
    customer = 'user.name',
    activation = 'expiryDate'
}

export default class ScalesVM implements ViewModel {

    onClose?: () => void;

    idToDelete: number|null = null;
    dataSet: IScale[] = [];
    confirmEnabled = false;
    modalOpened = false;
    modalHeader = "";
    modalContent: ReactNode = null;

    //Сортировка
    sortedColumn: SortableColumns = SortableColumns.id;
    sortDirection: SortDirections = 'ascending';

 
    debouncedFetch: _.DebouncedFunc<() => Promise<void>>;
    fetchData: () => Promise<void>;

    constructor(onClose?: () => void) {
        this.onClose = onClose;

        // Инициализация debounce-функции
        this.debouncedFetch = _.debounce(
            async () => {
                const data = await ScaleService.getList();
                this.dataSet = data;
            },
            300
        );

        // Публичный метод
        this.fetchData = () => this.debouncedFetch();

        makeAutoObservable(this, {
            // Явно исключаем не-observable поля
            debouncedFetch: false,
            fetchData: false
        });

    }

    // Computed property для отсортированных данных
    get sortedData(): IScale[] {
        return _.orderBy(
            this.dataSet,
            [this.sortedColumn],
            [this.sortDirection === 'ascending' ? 'asc' : 'desc']
        );
    }

    // Фильтрация (пример - можно расширить)
    filterData(searchTerm: string) {
        if (!searchTerm) return this.dataSet;

        return _.filter(this.dataSet, (item) =>
            _.includes(_.toLower(item.name), _.toLower(searchTerm)) ||
            _.includes(_.toString(item.id), searchTerm) ||
            _.includes(_.toLower(item.user?.name), _.toLower(searchTerm))
        );
    }

    // Оптимизированная сортировка
    sortTable = (column: SortableColumns) => {
        if (this.sortedColumn === column) {
            this.sortDirection = this.sortDirection === 'ascending'
                ? 'descending'
                : 'ascending';
        } else {
            this.sortedColumn = column;
            this.sortDirection = 'ascending';
        }
    }

    // Управление модальными окнами
    setModal = (header: string, content: ReactNode, isOpen = true) => {
        this.modalHeader = header;
        this.modalContent = content;
        this.modalOpened = isOpen;
    }

    // Управление подтверждением
    openConfirmDialog = (id: number) => {
        this.idToDelete = id;
        this.confirmEnabled = true;
    }

    closeConfirmDialog = () => {
        this.idToDelete = null;
        this.confirmEnabled = false;
    }

    // Операции с данными
    createScale = async () => {
        await ScaleService.createScale();
        await this.fetchData();
    }

    deleteScale = async () => {
        if (this.idToDelete !== null) {
            await ScaleService.deleteScale(this.idToDelete);
            await this.fetchData();
        }
    }

    // Отмена debounce при уничтожении
    dispose() {
        this.debouncedFetch.cancel();
    }
}
